<i18n>
{
  "en": {
    "title": "Schneider Sustainability Report Introduction",
    "description": "Sustainability is at the core of business growth. Hear from Schneider Electric CEO Jean-Pascal Tricoire, learn about Schneider Electric’s path to carbon neutrality, science-based targets, environmental strategy, and view our new 2018 – 2020 Schneider Sustainability Impact goals.",
    "introduction": {
      "section": "&nbsp;",
      "title": "Introduction: Our Mission",
      "welcome": {
        "quote": {
          "text": "We believe climate change can be overcome through innovation and collaboration. But the world must be proactive. We must act now. We must co-create a sustainable future.",
          "author": "Jean-Pascal Tricoire",
          "title": "Chairman & CEO,",
          "company": "Schneider Electric"
        },
        "txt1": {
          "title": "Welcome from the CEO",
          "para1": "At Schneider Electric, sustainability is part of everything we do &mdash; every decision, every innovation, and every initiative. For us, sustainability extends far beyond a vague hope for a cleaner planet. We believe that access to energy is a fundamental right, and therefore a truly relevant vision for sustainability must take into account human rights, ethics, and health and equity concerns.",
          "para2Subtitle": "The challenge of our time",
          "para2": "As we once again provide this report on our sustainability efforts, we’re reminded of the urgency of the climate crisis and inspired by those who, together, are rising to meet the challenge head on. Two years ago, the Paris Climate Agreement gave us all a clearer understanding of what’s at stake. Limiting global temperature rise this century to less than 2 &deg;C above pre-industrial levels is essential if we are to avoid widespread, disastrous ecological problems.",
          "para3Subtitle": "The path to a healthier planet",
          "para3": "We believe that concrete, meaningful action is the only successful path to address climate change and create a heathier world. That's what this report is about. Each year, our Schneider Sustainability Report shows how we’re taking steps to ensure our own sustainability; how we’re empowering our people; how we’re delivering sustainable innovations to the world; and how we’re supporting the sustainable development of others through outreach and initiatives.",
          "para4Subtitle": "Co-creating a sustainable future",
          "para4": "We also believe that sustainability is business &mdash; a core pillar for any successful company. This is because, in working for a more sustainable world, we not only ensure a healthier planet for future generations, we also promote innovation and prosperity here and now. Because Life Is On<sup>&trade;</sup> when life is powered and digital, we are empowering our partners and customers to accelerate the transition to a sustainable future in buildings, industry, data centers, and the grid. As I have said before, when it comes to the climate, I’m neither an optimist nor a pessimist. I’m an activist. At Schneider, we’ll continue to actively participate in all useful initiatives to increase the low-carbon approach to the world’s economy."
        },
        "pillars": {
          "intro": {
            "title": "Two global megatrends",
            "para": "Two megatrends &mdash; electrification and digitization &mdash; are presenting the world with the opportunity to make energy more decarbonized and more decentralized. The question is not if these changes will continue, but if the world will harness these opportunities to create a healthier, sustainable world."
          },
          "one": {
            "title": "Electrification",
            "para": "By 2040, consumption of electricity will increase by 60%.<sup>1</sup>"
          },
          "two": {
            "title": "Digitization",
            "para": "30 billion devices will be connected by 2020.<sup>2</sup>"
          }
        },
        "table": {
          "title": "The energy equation",
          "para": "Energy consumption and CO<sub>2</sub> emissions will continue to increase over the coming decades. The solution is efficiency. We must leverage technological innovation to use less energy, and to use the energy that we do consume more intelligently.",
          "row1": {
            "cell1": {
              "title": "1.5x"
            },
            "cell2": {
              "p1": "By 2050, energy consumption will grow by 50 percent."
            }
          },
          "row2": {
            "cell1": {
              "title": "÷2"
            },
            "cell2": {
              "p1": "By 2050, CO<sub>2</sub> emissions worldwide must be cut in half."
            }
          },
          "row3": {
            "cell1": {
              "title": "3x"
            },
            "cell2": {
              "p1": "By 2050, the world must become three times more efficient.",
              "footnote1": "1. Forecast for 2050 compared to 2009 levels",
              "footnote2": "2. Intergovernmental Panel on Climate Change (IPCC); IEA 2014 – 2015, Schneider Internal Analysis"
            }
          }
        },
        "txt2": {
          "title": "The energy paradox",
          "para1": "The energy equation exists alongside a surprising reality, what we call the energy paradox: We believe access to energy is a basic human right, yet we all must be mindful of the effects of energy consumption on the environment. The world must find a way to bring energy-poor populations access to energy while simultaneously fighting climate change. Here, again, initiative and proactive innovation is the answer."
        },
        "statement": {
          "title": "By 2050, energy consumption will increase by 50%.<sup>3</sup> But 1.1 billion people currently lack access to electricity.<sup>4</sup>"
        },
        "references": {
          "r1": "<sup>1</sup> IEA, World Energy Outlook 2016 ",
          "r2": "<sup>2</sup> IHS, March 2016",
          "r3": "<sup>3</sup> Increase of global energy consumption 2014 – 2040 in New Policy Scenario extrapolated to 2050. IEA, World Energy Outlook 2016",
          "r4": "<sup>4</sup> International Energy Agency, Energy Access Outlook 2017"
        }
      }
    }
  },
  "fr": {
    "title": "Schneider Sustainability Report Introduction",
    "description": "Sustainability is at the core of business growth. Hear from Schneider Electric CEO Jean-Pascal Tricoire, learn about Schneider Electric’s path to carbon neutrality, science-based targets, environmental strategy, and view our new 2018 – 2020 Schneider Sustainability Impact goals.",
    "introduction": {
      "section": "&nbsp;",
      "title": "Introduction: Notre Mission",
      "welcome": {
        "quote": {
          "text": "Nous pensons que l’innovation et la collaboration sont le remède au changement climatique. Mais pour cela, le monde doit se montrer proactif. Nous devons agir maintenant. Co-créons un avenir durable.",
          "author": "Jean-Pascal Tricoire",
          "title": "Président Directeur général,",
          "company": "Schneider Electric"
        },
        "txt1": {
          "title": "Message du Président",
          "para1": "Chez Schneider Electric, le développement durable est au cœur de chacune de nos actions, décisions, innovations et initiatives. Pour nous, le développement durable est bien plus qu’un vague espoir de rendre la planète plus propre. Nous pensons que l'accès à l’énergie est un droit fondamental. Par conséquent, une vision réellement pertinente du développement durable ne peut faire l’impasse sur les droits humains, sur l’éthique, ainsi que sur les questions liées à la santé et à l'équité.",
          "para2Subtitle": "Les défis du monde d'aujourd'hui",
          "para2": "Alors même que nous publions ce nouveau rapport sur nos progrès en matière de développement durable, l’urgence de la crise climatique se rappelle à nous, et nous tirons notre inspiration des acteurs qui, collectivement, émergent pour prendre ce défi à bras le corps. Il y a deux ans, l’Accord de Paris sur le climat nous a donné une meilleure vision des défis. Il est indispensable que nous limitions la hausse des températures mondiales à moins de 2 °C par rapport aux niveaux pré-industriels si nous voulons éviter une catastrophe écologique de grande ampleur.",
          "para3Subtitle": "Les solutions pour une planète plus saine",
          "para3": "Nous pensons que l’action concrète et engagée est la seule voie envisageable pour remédier au changement climatique et créer un monde en meilleure santé. Chaque année, le Schneider Sustainability report nous permet d’exposer les actions que nous menons pour garantir notre propre durabilité, les mesures que nous prenons en faveur de l’autonomie de nos collaborateurs, les innovations durables dont nous faisons profiter le monde et le soutien que nous apportons à d’autres acteurs dans leur démarche de développement durable.",
          "para4Subtitle": "Co-créer un futur durable",
          "para4": "Nous sommes également convaincus que développement durable rime avec développement économique, pilier fondamental de tout succès d’entreprise. En œuvrant pour un monde plus durable, nous ne faisons pas que préserver la santé de la planète pour les générations futures, nous promouvons l’innovation et la prospérité, ici et maintenant. Car ce qui est bon pour le climat est bon pour l’économie. Nous devons accélérer la transition vers une économie bas carbone, porteuse de nombreuses promesses : croissance économique durable, création d'emplois et amélioration de la santé publique."
        },
        "pillars": {
          "intro": {
            "title": "Deux grandes tendances mondiales",
            "para": "Deux lames de fond &mdash; l'électrification et la numérisation &mdash; offrent au monde la possibilité de produire l’énergie de manière décarbonée et décentralisée. La question n’est pas de savoir si ces changements vont se poursuivre, mais si le monde saisira les opportunités qui en découlent pour parvenir à un monde plus durable et plus sain."
          },
          "one": {
            "title": "Électrification",
            "para": "D’ici 2040, la consommation d’électricité aura augmenté de 60 %<sup>1</sup>."
          },
          "two": {
            "title": "Numérisation",
            "para": "D'ici 2020, ce sont 30 milliards d'équipements qui seront connectés<sup>2</sup>."
          }
        },
        "table": {
          "title": "L'équation énergétique",
          "para": "Dans les décennies à venir, la consommation d'énergie et les émissions de CO<sub>2</sub> vont continuer de croître. La solution ? L’efficacité. Nous devons miser sur l’innovation technologique pour utiliser moins d’énergie, et utiliser l’énergie que nous consommons de manière plus intelligente.",
          "row1": {
            "cell1": {
              "title": "1,5x"
            },
            "cell2": {
              "p1": "D’ici à 2050, la consommation d’énergie aura augmenté de 50 %"
            }
          },
          "row2": {
            "cell1": {
              "title": "÷2"
            },
            "cell2": {
              "p1": "D'ici à 2050, nous devons diviser par 2 les émissions de CO<sub>2</sub> dans le monde"
            }
          },
          "row3": {
            "cell1": {
              "title": "3x"
            },
            "cell2": {
              "p1": "D’ici 2050, le monde doit tripler son efficacité",
              "footnote1": "1. Prévision pour 2050 comparée aux niveaux de 2009",
              "footnote2": "2. Groupe d'experts intergouvernemental sur l'évolution du climat (GIEC) ; AIE 2014-2015, étude interne de Schneider Electric"
            }
          }
        },
        "txt2": {
          "title": "Le paradoxe énergétique",
          "para1": "L'équation énergétique côtoie une réalité surprenante, que nous appelons le « paradoxe énergétique ». Nous pensons que l'accès à l'énergie est un droit humain fondamental, mais nous devons être attentifs aux  effets de la consommation d'énergie sur l’environnement. Le monde doit trouver une façon de donner accès à l’énergie aux populations qui en sont privées, tout en combattant le changement climatique. Là encore, la solution réside dans l’esprit d’initiative et l’innovation proactive."
        },
        "statement": {
          "title": "D’ici 2050, la consommation d’énergie aura augmenté de 50% <sup>3</sup>. Mais 1,1 milliard de personnes n’ont toujours pas accès à l’électricité <sup>4</sup>."
        },
        "references": {
          "title": "Références",
          "r1": "<sup>1</sup> IEA, World Energy Outlook 2016",
          "r2": "<sup>2</sup> IHS, March 2016",
          "r3": "<sup>3</sup> Increase of global energy consumption 2014 – 2040 in New Policy Scenario extrapolated to 2050. IEA, World Energy Outlook 2016",
          "r4": "<sup>4</sup> International Energy Agency, Energy Access Outlook 2017"
        }
      }
    }
  }
}
</i18n>

<template>
  <div id="Introduction_Welcome">
    <Hero :loResSrc="require('@/assets/img/introduction/introduction-low.jpg')"
      :hiResSrc="require('@/assets/img/introduction/introduction.jpg')"
      chapter="01" :section="$t('introduction.section')" :title="$t('introduction.title')" :grayBG="true" />

    <Quote :photo="require('@/assets/img/introduction/introduction-welcome-quote.jpg')" :name="$t('introduction.welcome.quote.author')" :title="$t('introduction.welcome.quote.title')" :company="$t('introduction.welcome.quote.company')">
      <div v-html="$t('introduction.welcome.quote.text')"></div>
    </Quote>

    <section class="m-b-120 m-t-120">
      <Txt>
        <h2 v-html="$t('introduction.welcome.txt1.title')"></h2>
        <p v-html="$t('introduction.welcome.txt1.para1')"></p>
        <h3 v-html="$t('introduction.welcome.txt1.para2Subtitle')"></h3>
        <p v-html="$t('introduction.welcome.txt1.para2')"></p>
        <h3 v-html="$t('introduction.welcome.txt1.para3Subtitle')"></h3>
        <p v-html="$t('introduction.welcome.txt1.para3')"></p>
        <h3 v-html="$t('introduction.welcome.txt1.para4Subtitle')"></h3>
        <p v-html="$t('introduction.welcome.txt1.para4')" class="m-0"></p>
      </Txt>
    </section>

    <section class="pad-120 bg-darkergrey c-palegrey">
      <txt class="m-b-32">
        <h2 id="megatrends" v-html="$t('introduction.welcome.pillars.intro.title')"></h2>
        <p v-html="$t('introduction.welcome.pillars.intro.para')"></p>
      </txt>
      <Pillar :picture="require('../../assets/img/introduction/introduction-welcome-electrification.jpg')"
        :title="$t('introduction.welcome.pillars.one.title')"
        :content="$t('introduction.welcome.pillars.one.para')"/>
      <Pillar :align-right="true" :picture="require('../../assets/img/introduction/introduction-welcome-digitization.jpg')"
        :title="$t('introduction.welcome.pillars.two.title')"
        :content="$t('introduction.welcome.pillars.two.para')"/>
    </section>

    <section class="l-bg-white l-section-pad">
      <Table columns="2" class="m-b-32">
        <div slot="above">
          <h2 id="energyequation" v-html="$t('introduction.welcome.table.title')"/>
          <p v-html="$t('introduction.welcome.table.para')"/>
        </div>
        <TableRow>
          <TableCell :bgimg="require('../../assets/img/introduction/introduction-welcome-880937538.jpg')">
            <h4 class="t-white" v-html="$t('introduction.welcome.table.row1.cell1.title')"></h4>
          </TableCell>
          <TableCell>
            <p v-html="$t('introduction.welcome.table.row1.cell2.p1')" />
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell >
            <!-- <h4 class="t-green-light" v-html="$t('introduction.welcome.table.row2.cell1.title')"></h4> -->
            <h4 class="t-green-light" v-html="$t('introduction.welcome.table.row2.cell1.title')"></h4>
          </TableCell>
          <!-- <TableCell bgc="#3dcd58"> -->
          <TableCell :bgimg="require('../../assets/img/introduction/introduction-welcome-170100548.jpg')">
            <p class="t-white" v-html="$t('introduction.welcome.table.row2.cell2.p1')" />
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('../../assets/img/introduction/introduction-welcome-895957656.jpg')">
            <h4 class="t-white" v-html="$t('introduction.welcome.table.row3.cell1.title')"></h4>
          </TableCell>
          <TableCell>
            <p v-html="$t('introduction.welcome.table.row3.cell2.p1')" />
          </TableCell>
        </TableRow>
      </Table>
      <References>
        <Reference v-html="$t('introduction.welcome.table.row3.cell2.footnote1')" />
        <Reference v-html="$t('introduction.welcome.table.row3.cell2.footnote2')" />
      </References>
    </section>

    <section class="m-b-120 m-t-120">
      <Txt class="m-b-32">
        <h2 id="energyparadox" v-html="$t('introduction.welcome.txt2.title')"></h2>
        <p v-html="$t('introduction.welcome.txt2.para1')"></p>
      </Txt>
      <Statement class="m-b-30">
        <h3 v-html="$t('introduction.welcome.statement.title')"></h3>
      </Statement>
    </section>

    <section class="m-b-120 m-t-120">
      <ImageFull>
        <img src="@/assets/img/introduction/introduction-welcome-energy-paradox.jpg" />
      </ImageFull>
    </section>

    <References>
      <Reference v-html="$t('introduction.welcome.references.r1')" />
      <Reference v-html="$t('introduction.welcome.references.r2')" />
      <Reference v-html="$t('introduction.welcome.references.r3')" />
      <Reference v-html="$t('introduction.welcome.references.r4')" />
    </References>

    <FooterNav
      nextRoute="Introduction_SustainabilityAtSchneiderElectric"
      prevRoute="Homepage" />
  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import YouTubeVideo from '@/components/YouTubeVideo'
import Quote from '@/components/Quote'
import Txt from '@/components/Txt'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Snapshot from '@/components/Snapshot'
import SnapshotCard from '@/components/SnapshotCard'
import References from '@/components/References'
import Reference from '@/components/Reference'
import ImageFull from '@/components/ImageFull'
import Pillar from '@/components/Pillar'
import Barometer from '@/components/Barometer'
import BarometerIndicator from '@/components/BarometerIndicator'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'

export default {
  name: 'Introduction_Welcome',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    YouTubeVideo,
    Quote,
    Txt,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    Snapshot,
    SnapshotCard,
    References,
    Reference,
    ImageFull,
    Pillar,
    Barometer,
    BarometerIndicator,
    Table,
    TableRow,
    TableCell
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/introduction/welcome.vue